import type { EntitlementKey } from "./entitlements/modules";

export type SystemRole = 'platform_admin' | 'owner' | 'admin' | 'developer' | 'operator' | 'viewer' | 'end_user';

export interface UserContext {
  sub: string;
  email: string;
  role: SystemRole;
  orgId: string;
  endOrgId?: string;
  orgType?: string;
  accountName?: string;
  displayName?: string;
  /**
   * True when `role` came from somewhere authoritative — a `role` claim actually
   * present on the token, or a server response that states it.
   *
   * False/absent means `role` is the least-privilege FALLBACK, not an answer. A
   * brand-new signup's token is minted before provisioning writes the org and
   * role onto the Keycloak user, so it carries neither; `role` still has to hold
   * a SystemRole for every gate that reads it, so it falls back to viewer. Without
   * this flag that guess is indistinguishable from a token that really says
   * viewer, which is what left a brand-new owner in a read-only dashboard: the
   * guess was never revisited once the memberships response arrived with the
   * real answer.
   */
  roleResolved?: boolean;
  /**
   * The signed token names a platform_admin (realm role or role claim). `role`
   * alone cannot say so: in a customer org it is their membership role there,
   * as the gateway stamps it. For the few gates about the PERSON rather than
   * the org they act in, such as never asking them the signup questions.
   */
  isPlatformAdmin?: boolean;
}

const ROLE_HIERARCHY: Record<SystemRole, number> = {
  platform_admin: 8, owner: 7, admin: 6, developer: 5, operator: 4,
  viewer: 2, end_user: 1,
};

/**
 * Role floor for Settings -> Workflows Tags.
 *
 * Shared by the sidebar entry, the nav map and the pane's own gate. /settings/*
 * is gated at "operator" in App.tsx, which is lower, so the pane has to gate
 * itself — and three independent copies of "developer" is exactly how a pane
 * ends up reachable by a role that 403s on every button.
 */
export const WORKFLOW_TAGS_MIN_ROLE: SystemRole = 'developer';

export function hasMinRole(userRole: SystemRole, minRole: SystemRole): boolean {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[minRole];
}

export function getRoleLabel(role: SystemRole): string {
  const labels: Record<SystemRole, string> = {
    platform_admin: 'Platform Admin',
    owner: 'Owner',
    admin: 'Admin',
    developer: 'Developer',
    operator: 'Operator',
    viewer: 'Viewer',
    end_user: 'End User',
  };
  return labels[role] || role;
}

/**
 * Tier colour for a role, as a design-system token.
 *
 * The previous seven hand-picked hues were seven accent colours, which the
 * design system does not have — it ships one accent plus three chart series.
 * Roles are grouped by privilege tier onto those instead:
 *
 *   platform  chart-3   platform_admin
 *   account   chart-1   owner, admin
 *   builder   chart-2   developer
 *   tenant    text-body operator, viewer
 *   embed     text-muted end_user
 *
 * Roles inside a tier share a colour deliberately. Every caller renders
 * `getRoleLabel()` alongside, so the label carries the meaning and colour is
 * reinforcement — which is also the accessibility rule: never colour alone.
 */
export function getRoleColor(role: SystemRole): string {
  const colors: Record<SystemRole, string> = {
    platform_admin: 'var(--fs-chart-3)',
    owner: 'var(--fs-chart-1)',
    admin: 'var(--fs-chart-1)',
    developer: 'var(--fs-chart-2)',
    operator: 'var(--fs-text-body)',
    viewer: 'var(--fs-text-body)',
    end_user: 'var(--fs-text-muted)',
  };
  return colors[role] || 'var(--fs-text-muted)';
}

/** Translucent tint of the role colour, for badge backgrounds. */
export function getRoleTint(role: SystemRole, percent = 12): string {
  return `color-mix(in srgb, ${getRoleColor(role)} ${percent}%, transparent)`;
}

// Nav items visible per role
export interface NavItem {
  path: string;
  label: string;
  /** Rail children. Only Settings has them: it is a menu of unrelated pages
   *  with no page of its own. Every other rail item is a page. */
  children?: NavItem[];
  minRole?: SystemRole;
  roles?: SystemRole[];
  /** Hide the item unless the org holds this entitlement. Resolved by the
   *  Sidebar; a nav entry that leads somewhere the org cannot use is the same
   *  dead end as a button that 403s. */
  entitlement?: EntitlementKey;
  section?: 'main' | 'settings';
  group?: string;
}

// ── Admin console nav (platform_admin, /admin/* routes) ──
//
// Not rendered by this dashboard: fastn-platform-admin serves the console. The
// list is kept as this repo's model of that console (CLAUDE.md rule 19), so a
// partner-facing page being removed here cannot silently drop the console's
// entry for the same object. byo-oauth-discoverability.test.ts pins it.

export function getAdminMainNavItems(): NavItem[] {
  return [
    { path: '/admin', label: 'Overview', section: 'main' },
    { path: '/admin/integrations', label: 'Integrations', section: 'main' },
    { path: '/admin/connector-builder', label: 'Connector Builder', section: 'main' },
    { path: '/admin/accounts', label: 'Accounts', section: 'main' },
    { path: '/admin/onboarding-reviews', label: 'Reviews', section: 'main' },
    { path: '/admin/audit', label: 'Audit Log', section: 'main' },
    { path: '/admin/feedback', label: 'Feedback', section: 'main' },
    { path: '/admin/ai-costs', label: 'AI Costs', section: 'main' },
    { path: '/admin/metrics', label: 'Metrics', section: 'main' },
    { path: '/admin/billing', label: 'Billing', section: 'main' },
    { path: '/admin/health', label: 'System Health', section: 'main' },
    { path: '/admin/ai-models', label: 'AI Models', section: 'main' },
    { path: '/admin/unified-catalog', label: 'Unified API', section: 'main' },
    { path: '/admin/entitlements', label: 'Entitlements', section: 'main' },
    { path: '/admin/v1-workspace', label: 'v1 Workspace Bridge', section: 'main' },
    { path: '/admin/oauth-apps', label: 'OAuth apps', section: 'main' },
    { path: '/admin/platform-users', label: 'Platform Users', section: 'main' },
    { path: '/admin/settings', label: 'Settings', section: 'main' },
  ];
}

// ── Sidebar nav (grouped) ──

const ADMIN_ROLES: SystemRole[] = ['platform_admin', 'owner', 'admin'];
const DEVELOPER_ROLES: SystemRole[] = ['platform_admin', 'owner', 'admin', 'developer'];

/**
 * Who manages the product's webhooks (Connect your product > Webhooks): the roles the
 * event-subscriptions API admits (MANAGER_ROLES in services/api/src/routes/event-subscriptions.ts).
 * The page itself also opens for operators, who see the API connector tab only.
 */
export function canManageProductWebhooks(role: SystemRole): boolean {
  return DEVELOPER_ROLES.includes(role);
}

/**
 * Settings pages in rail order. The rail's Settings menu and the page Settings
 * lands on both read this one list, so they cannot drift apart.
 */
const SETTINGS_PAGES: NavItem[] = [
  { path: '/settings/people', label: 'People', roles: ADMIN_ROLES },
  { path: '/settings/general', label: 'General', roles: ADMIN_ROLES },
  { path: '/settings/billing', label: 'Billing', roles: ADMIN_ROLES },
  { path: '/settings/roles', label: 'Roles', roles: ADMIN_ROLES },
  { path: '/settings/audit', label: 'Audit log', roles: ADMIN_ROLES },
  { path: '/settings/environments', label: 'Environments', roles: DEVELOPER_ROLES },
  { path: '/settings/secrets', label: 'Secrets', roles: DEVELOPER_ROLES },
  { path: '/settings/configs', label: 'Configs', roles: DEVELOPER_ROLES },
  { path: '/settings/database', label: 'Database', roles: DEVELOPER_ROLES },
  { path: '/settings/workflows-tags', label: 'Workflow tags', minRole: WORKFLOW_TAGS_MIN_ROLE },
  { path: '/settings/trash', label: 'Trash', roles: DEVELOPER_ROLES },
];

/** The first Settings page this role can open, or null when it can open none. */
export function getDefaultSettingsPath(role: SystemRole): string | null {
  return SETTINGS_PAGES.find((item) => canSeeNavItem(item, role))?.path ?? null;
}

/**
 * The left rail: Home, then the three steps the website sells in the order it
 * sells them (Build, Embed, Operate), then Settings.
 *
 * Every item in a step group is a page. Sections inside a page are tabs on
 * that page, never rail children: a parent row that links to its first child
 * is what made "Integrations" open the agent for one role and the connector
 * catalog for another. Settings is the one branch, because it is a menu of
 * unrelated pages and has no page of its own.
 *
 * Placements that are deliberate:
 *  - Connections is under Operate, not Build. A connection is a signed-in account,
 *    created by a customer in the widget or by the partner for their own
 *    systems; nobody designs one.
 *  - Triggers and Unified APIs are tabs on Integrations, since they are facets
 *    of the workflow being built rather than things to go and find.
 *  - Health absorbs Pending updates and Reporting: the vendor patches, the
 *    broken connections and the failing workflows are the three queues the
 *    maintainer agent works, and they belong on one page.
 *  - API keys and the MCP server sit under Embed as one page: a developer
 *    needs both at the moment they embed, and neither is an account setting.
 *  - The partner's own product is its own Build row, Connect your product,
 *    because the Integrations page is the third-party catalog. It covers an
 *    API, webhooks, or both, and is required to go live.
 *
 * Role gates are carried over verbatim from the pages' own gates - the rail
 * must never surface a page the role cannot open.
 */
export interface NavGroup {
  /** Section heading. null renders the items with no heading, as Home does. */
  label: string | null;
  items: NavItem[];
}

const NAV_ROLES_ALL_DASHBOARD: SystemRole[] = ['platform_admin', 'owner', 'admin', 'developer', 'operator', 'viewer'];

export function getSidebarNavGroups(role: SystemRole): NavGroup[] {
  const settingsPath = getDefaultSettingsPath(role);

  const groups: NavGroup[] = [
    { label: null, items: [{ path: '/', label: 'Home', section: 'main' }] },
    {
      label: 'Build',
      items: [
        // Owns its own full-height layout, so it stays at /agent. The same
        // builder is also reached from the Home prompt and from "Build with
        // AI" on a connector; this row is for coming back to past builds.
        {
          path: '/agent',
          label: 'Build with AI',
          roles: ['platform_admin', 'owner', 'admin', 'developer', 'operator'],
          entitlement: 'aiBuilder.integrations',
          section: 'main',
        },
        // The partner's own product, through its API or webhooks. Its own row,
        // not a tab on Integrations: that page is the third-party catalog,
        // and this is the one thing in it that is theirs. Phrased as a step,
        // like Build with AI. Required to go live, never labeled optional.
        { path: '/your-product', label: 'Connect your product', roles: ADMIN_ROLES, section: 'main' },
        { path: '/integrations', label: 'Integrations', section: 'main' },
      ],
    },
    {
      label: 'Embed',
      items: [
        { path: '/widgets', label: 'Widget', roles: NAV_ROLES_ALL_DASHBOARD, section: 'main' },
        { path: '/access', label: 'API keys and MCP', roles: DEVELOPER_ROLES, section: 'main' },
      ],
    },
    {
      label: 'Operate',
      items: [
        // First in Operate: is everything healthy? The maintainer agent's work
        // shows up here (what broke, what changed upstream, the fix it
        // proposes), and the page says so in its description.
        { path: '/health', label: 'Health', roles: DEVELOPER_ROLES, section: 'main' },
        { path: '/customers', label: 'Customers', roles: ADMIN_ROLES, section: 'main' },
        { path: '/connections', label: 'Connections', section: 'main' },
        { path: '/activity', label: 'Activity', roles: NAV_ROLES_ALL_DASHBOARD, section: 'main' },
      ],
    },
  ];

  // Settings resolves to the first page this role can actually open, so the
  // rail never links a role into a denied page. Unlabelled, like Home: the
  // Settings row already names the section and its menu is underneath it.
  if (settingsPath) {
    groups.push({
      label: null,
      items: [{
        path: settingsPath,
        label: 'Settings',
        section: 'main',
        children: SETTINGS_PAGES.filter((item) => canSeeNavItem(item, role)),
      }],
    });
  }

  return groups
    .map((group) => ({
      ...group,
      items: group.items
        .filter((item) => canSeeNavItem(item, role))
        .map((item) => item.children
          ? { ...item, children: item.children.filter((kid) => canSeeNavItem(kid, role)) }
          : item),
    }))
    .filter((group) => group.items.length > 0);
}

function canSeeNavItem(item: NavItem, role: SystemRole): boolean {
  if (item.roles) return item.roles.includes(role);
  if (item.minRole) return hasMinRole(role, item.minRole);
  return true;
}
